import { Box } from '@mui/material';
import { SEOSegmentationPageType } from '@/__generated__/globalTypes';
import { getSearchResults_seoSegmentationSearchResults_SEOSegmentationSearchResultsSuccess as SEOSegmentationSearchResultsSuccessType } from '@/__generated__/getSearchResults';
import useFeatureFlag from '@/components/hooks/useFeatureFlag';
import { HTL_SLOTS, SEARCH_RESULT_TYPE, UI_FEATURE_FLAGS } from '@/constants';
import JobSearchResult from '@/components/SearchResults/JobSearchResult';
import SeoConversionBox from '@/components/SeoConversionBox';
import { useResponsive } from '../hooks/useResponsive';
import { useLDClientFlags } from '../hooks/useLDClientFlags';
import { GAMAds } from '../GAMAds';

interface JobSearchResultsProps {
  results: SEOSegmentationSearchResultsSuccessType['jobSearchResults'];
  conversionHeader?: string | null;
  enrollmentURL?: string;
  resultType: keyof typeof SEARCH_RESULT_TYPE;
  isClientSearchResultsQuery: boolean;
  showConversionSectionFrequency?: number;
  pageType: SEOSegmentationPageType;
}

const JobSearchResults = ({
  conversionHeader = null,
  enrollmentURL,
  isClientSearchResultsQuery,
  pageType,
  resultType,
  results,
  showConversionSectionFrequency = 0,
}: JobSearchResultsProps) => {
  const { isSmallViewport, isMediumViewport, isLargeViewport } = useResponsive();
  const { SEO_3487_GAM_ADS_SEO_MFE: GAMFlag } = useLDClientFlags();
  const showGAMAds = GAMFlag?.variationIndex === 1;
  const gamAdIndexPositions = [1, 6, 11];
  const gamAdSlotNames = [
    HTL_SLOTS.SEO_DIRECTORY_INLINE_1,
    HTL_SLOTS.SEO_DIRECTORY_INLINE_2,
    HTL_SLOTS.SEO_DIRECTORY_INLINE_3,
  ];

  const aboveTheFoldTestJobsFlag = useFeatureFlag(
    UI_FEATURE_FLAGS.SEO_3217_ABOVE_THE_FOLD_CONVERSION_TEST_PROVIDERS.id
  );
  const isAboveTheFoldTestJobs = aboveTheFoldTestJobsFlag?.variationIndex === 2;

  const targetValue = isSmallViewport || isMediumViewport ? '' : '_blank';

  const showConversionCTASection = (index: number) => {
    return (
      results.length > showConversionSectionFrequency &&
      (index + 1) % showConversionSectionFrequency === 0 &&
      results.length - 1 !== index
    );
  };

  const mappedResults = results.map((searchResult, index: number) => {
    return (
      <Box key={searchResult.jobDetailURL}>
        <Box mb={5}>
          {searchResult.jobDetailURL && (
            <a
              href={searchResult.jobDetailURL}
              target={targetValue}
              aria-label="profile-link"
              style={{ textDecoration: 'none' }}>
              <JobSearchResult
                job={searchResult.seoSegmentationJob}
                isClientSearchResultsQuery={isClientSearchResultsQuery}
              />
            </a>
          )}
        </Box>
        {showConversionCTASection(index) && !isAboveTheFoldTestJobs && (
          <Box data-testid="SeoConversionCTAs" my={4} mx={-2} bgcolor="grey.100">
            <SeoConversionBox
              pageType={pageType}
              resultType={resultType}
              header={conversionHeader}
              enrollmentURL={enrollmentURL}
            />
          </Box>
        )}
      </Box>
    );
  });

  const insertAdsAtPositions = (list: JSX.Element[], ads: JSX.Element[], positions: number[]) => {
    const newList = [...list];
    positions.forEach((position, index) => {
      // only insert if the position is less than the length of the array
      if (position <= newList.length) {
        newList.splice(position + index, 0, ads[index]);
      }
    });

    return newList;
  };

  if (showGAMAds && (isSmallViewport || isMediumViewport)) {
    const gamAds: JSX.Element[] = gamAdSlotNames.map((slotName) => (
      <GAMAds key={slotName} slotName={slotName} />
    ));

    const listingWithAds = insertAdsAtPositions(mappedResults, gamAds, gamAdIndexPositions);

    return <Box>{listingWithAds}</Box>;
  }

  if (showGAMAds && isLargeViewport) {
    const gamAds: JSX.Element[] = [
      <GAMAds key={HTL_SLOTS.SEO_DIRECTORY_INLINE_1} slotName={HTL_SLOTS.SEO_DIRECTORY_INLINE_1} />,
    ];
    // place ad after the 10th profile card
    const gamAdIndexPos = [11];

    return <Box>{insertAdsAtPositions(mappedResults, gamAds, gamAdIndexPos)}</Box>;
  }

  return <Box>{mappedResults}</Box>;
};

export default JobSearchResults;
